import { Plus, Trash } from "@phosphor-icons/react"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { toast } from "@/components/feedback/Toast"
import { Button } from "@/components/ui/Button"
import { ConfirmDialog } from "@/components/ui/ConfirmDialog"
import { IconButton } from "@/components/ui/IconButton"
import { Skeleton } from "@/components/ui/Skeleton"
import { CniFeatureCreateDialog } from "@/features/cni-features/components/CniFeatureCreateDialog"
import {
  useCniFeatures,
  useDeleteCniFeature,
} from "@/features/cni-features/hooks/useCniFeatures"
import type { CniFeature } from "@/features/cni-features/schemas"
import { useCniPackages } from "@/features/cni-packages/hooks/useCniPackages"
import type { CniPackageSlug } from "@/features/cni-packages/schemas"

/**
 * `/cni/features` — özellik kataloğu (system + custom CRUD).
 *
 * Sistem feature'ları silinemez; her satırda ✓/— ile hangi paketlerde olduğu
 * gösterilir. Custom feature'lar Dalga 3 ile eklenir, satır sonunda silme
 * butonu çıkar. Yeni feature, otomatik olarak paket edit dialog'unda da
 * checkbox olarak görünür.
 */
export const Route = createFileRoute("/_authed/cni/features")({
  component: CniFeaturesPage,
})

function CniFeaturesPage() {
  const { t } = useTranslation("cni-features")
  const { t: tCommon } = useTranslation("common")
  const { t: tPackages } = useTranslation("cni-packages")
  const featuresQ = useCniFeatures()
  const packagesQ = useCniPackages()
  const deleteM = useDeleteCniFeature()

  const [createOpen, setCreateOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<CniFeature | null>(null)

  const grouped = useMemo(() => {
    const map = new Map<string, CniFeature[]>()
    for (const f of featuresQ.data ?? []) {
      const list = map.get(f.category) ?? []
      list.push(f)
      map.set(f.category, list)
    }
    return Array.from(map.entries())
  }, [featuresQ.data])

  const isInPackage = (featureKey: string, pkgSlug: CniPackageSlug): boolean => {
    const pkg = packagesQ.data?.find((p) => p.slug === pkgSlug)
    return pkg?.features.includes(featureKey) ?? false
  }

  const handleDelete = async () => {
    if (!deleteTarget) {
      return
    }
    try {
      await deleteM.mutateAsync(deleteTarget.key)
      toast.success(t("deleteFeature.toast.success"))
      setDeleteTarget(null)
    } catch (e) {
      toast.error(t("deleteFeature.toast.error"), (e as Error).message)
    }
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="border-b border-ok-border px-6 py-4 shrink-0 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t("title")}</h1>
          <p className="text-xs text-ok-foreground-muted">{t("subtitle")}</p>
        </div>
        <Button intent="primary" size="sm" onClick={() => setCreateOpen(true)}>
          <Plus size={14} weight="bold" />
          {t("createFeature.button")}
        </Button>
      </div>
      <div className="flex-1 overflow-auto p-6 space-y-6">
        <div className="rounded-ok border border-ok-border bg-ok-bg-subtle p-3 text-xs text-ok-foreground-muted">
          {t("note")}
        </div>

        {featuresQ.isLoading ? (
          <Skeleton className="h-64 w-full rounded-ok" />
        ) : featuresQ.error ? (
          <p className="text-sm text-ok-critical">
            {t("loadError")}: {(featuresQ.error as Error).message}
          </p>
        ) : (
          grouped.map(([category, features]) => (
            <section key={category} className="space-y-3">
              <h2 className="text-sm uppercase tracking-wide text-ok-foreground-subtle font-semibold">
                {t(`categories.${category}`, { defaultValue: category })}
              </h2>
              <div className="rounded-ok border border-ok-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-ok-bg-muted">
                    <tr>
                      <th className="text-left p-3 font-semibold text-xs">
                        {t("columns.feature")}
                      </th>
                      <th className="text-center p-3 font-semibold text-xs">
                        {tPackages("packages.basic.name")}
                      </th>
                      <th className="text-center p-3 font-semibold text-xs">
                        {tPackages("packages.advanced.name")}
                      </th>
                      <th className="text-center p-3 font-semibold text-xs">
                        {tPackages("packages.full.name")}
                      </th>
                      <th className="p-3 w-12" aria-label="actions" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ok-border">
                    {features.map((f) => (
                      <tr key={f.key}>
                        <td className="p-3">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium">{f.label}</span>
                              {!f.isSystem ? (
                                <span className="rounded-ok-sm bg-ok-info-soft px-1 py-0 text-[10px] uppercase tracking-wide text-ok-info">
                                  {t("custom.badge")}
                                </span>
                              ) : null}
                            </div>
                            <span className="font-mono text-[11px] text-ok-foreground-subtle">
                              {f.key}
                            </span>
                            {f.description ? (
                              <span className="text-xs text-ok-foreground-muted">
                                {f.description}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          {isInPackage(f.key, "basic") ? (
                            <span className="text-ok-success">✓</span>
                          ) : (
                            <span className="text-ok-foreground-subtle">—</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {isInPackage(f.key, "advanced") ? (
                            <span className="text-ok-success">✓</span>
                          ) : (
                            <span className="text-ok-foreground-subtle">—</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {isInPackage(f.key, "full") ? (
                            <span className="text-ok-success">✓</span>
                          ) : (
                            <span className="text-ok-foreground-subtle">—</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {!f.isSystem ? (
                            <IconButton
                              intent="ghost"
                              aria-label={t("deleteFeature.button")}
                              onClick={() => setDeleteTarget(f)}
                            >
                              <Trash size={14} weight="bold" />
                            </IconButton>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))
        )}
      </div>

      <CniFeatureCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title={t("deleteFeature.title")}
        description={t("deleteFeature.description", {
          key: deleteTarget?.key ?? "",
        })}
        confirmLabel={tCommon("actions.delete")}
        cancelLabel={tCommon("actions.cancel")}
        intent="destructive"
        onConfirm={handleDelete}
        loading={deleteM.isPending}
      />
    </div>
  )
}
